import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { MenuContainer, MenuItem, PortalAnchor } from './styles';


/**
 * Reusable dropdown menu with icon support for consistent menu styling.
 *
 * Positioning (when `portal` is true):
 *   - default: an internal hidden anchor span tracks the parent container
 *   - `anchorRef`: external element ref to position against
 *   - `position`: viewport coords `{x, y}` for click-anchored context menus
 *     (overrides `anchorRef`). `anchor` controls which corner sits at the
 *     point — "right" means the menu's right edge sits at x.
 *   - `placement="bottom"` (default) opens below the anchor;
 *     `placement="top"` opens above (e.g. menus pinned to the bottom of
 *     the viewport such as the player bar).
 *
 * Item layout:
 *   - `itemAlign="right"` (default): rows right-justified
 *   - `itemAlign="left"`: rows left-justified (mirror image)
 *   - `iconPosition`: "leading" (icon before label) | "trailing" (icon after
 *     label). Defaults to mirror `itemAlign` ("left" → leading, "right" →
 *     trailing) so a single prop covers the common case; pass it explicitly
 *     to decouple icon order from justification.
 */
const DropdownMenu = ({
  open,
  onClose,
  items = [],
  className = '',
  minWidth = undefined,
  anchor = 'right',
  itemAlign = 'right',
  iconPosition = itemAlign === 'left' ? 'leading' : 'trailing',
  autoTop = false,
  portal = false,
  placement = 'bottom',
  iconSize = 'sm',
  anchorRef: externalAnchorRef = null,
  position = null,
}) => {
  const theme = useTheme();
  const ref = useRef(null);
  const internalAnchorRef = useRef(null);
  const activeAnchorRef = externalAnchorRef || internalAnchorRef;
  const [portalStyle, setPortalStyle] = useState(null);

  const renderItem = (item) => {
    const key = `${item.type ?? 'item'}-${item.icon}`;
    const iconNode = item.icon && (
      <Icon
        key="icon"
        type={item.icon}
        size={iconSize}
        color="inherit"
      />
    );
    const labelNode = <span key="label">{item.label}</span>;
    const node = (
      <MenuItem
        key={key}
        $disabled={item.disabled}
        $selected={item.selected}
        $align={itemAlign}
        style={{ '--hover-color': theme.textLight }}
        onClick={() => {
          if (item.disabled || !item.onClick) return;
          item.onClick();
          if (!item.keepOpen) onClose();
        }}
      >
        {iconPosition === 'leading' ? [iconNode, labelNode] : [labelNode, iconNode]}
      </MenuItem>
    );

    // Only show a tooltip when the item is disabled (and has tooltip text);
    // otherwise the label text already conveys the action.
    const disabledTip = item.disabled && (item.disabledTooltip ?? item.tooltipText);
    if (disabledTip) {
      return (
        <Tooltip key={key} title={disabledTip} placement={itemAlign === 'left' ? 'right' : 'left'}>
          {node}
        </Tooltip>
      );
    }
    return node;
  };

  // Compute fixed position when portalling
  const updatePosition = useCallback(() => {
    if (!portal) return;
    const gap = parseInt(theme.dropdownMenu.gap, 10) || 0;
    const style = {
      position: 'fixed',
      zIndex: 9999,
      width: 'auto',
      marginTop: 0,
    };

    // Click-anchored (context menu): pin a corner to the click point
    if (position) {
      style.top = position.y;
      if (anchor === 'left') {
        style.left = position.x;
        style.right = 'auto';
      } else {
        style.right = window.innerWidth - position.x;
        style.left = 'auto';
      }
      setPortalStyle(style);
      return;
    }

    // Element-anchored: position relative to anchor element's bounding rect
    const anchorEl = activeAnchorRef.current;
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    if (placement === 'top') {
      style.bottom = window.innerHeight - rect.top + gap;
      style.top = 'auto';
    } else {
      style.top = rect.bottom + gap;
    }
    if (anchor === 'left') {
      style.left = rect.left;
      style.right = 'auto';
    } else {
      style.right = window.innerWidth - rect.right;
      style.left = 'auto';
    }
    setPortalStyle(style);
  }, [portal, anchor, placement, theme, position, activeAnchorRef]);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, portal, updatePosition]);

  // click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // Treat the anchor element as "inside" so its own onClick can toggle
      // the menu closed (otherwise we'd close here then the anchor click
      // would immediately re-open it).
      const anchorEl = activeAnchorRef.current;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose, activeAnchorRef]);

  // Always render the portal anchor so we have a DOM ref for positioning
  if (portal) {
    const menu = open && portalStyle ? (
      <MenuContainer
        ref={ref}
        className={className}
        $minWidth={minWidth}
        $anchor={anchor}
        $autoTop={autoTop}
        style={portalStyle}
      >
        {items.map(renderItem)}
      </MenuContainer>
    ) : null;

    // When the consumer supplies anchorRef or position, no internal anchor
    // span is needed — they own the positioning source.
    const needsInternalAnchor = !externalAnchorRef && !position;

    return (
      <>
        {needsInternalAnchor && <PortalAnchor ref={internalAnchorRef} />}
        {menu && ReactDOM.createPortal(menu, document.body)}
      </>
    );
  }

  if (!open) return null;

  return (
    <MenuContainer ref={ref} className={className} $minWidth={minWidth} $anchor={anchor} $autoTop={autoTop}>
      {items.map(renderItem)}
    </MenuContainer>
  );
};

export default DropdownMenu;
